import {
  ChangeDetectorRef,
  Directive,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewContainerRef,
  inject,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { TranslationModule } from './i18n.config';
import { TranslationManagerService } from './translation-manager.service';

/**
 * Структурна директива: підвантажує модуль перекладу і рендерить вміст,
 * коли його неймспейси вже в словнику.
 *
 *   <ng-container *appTranslationModule="'olx'">
 *     {{ 'olx.title' | translate }}
 *   </ng-container>
 *
 * Потрібна там, де порожній текст на частку секунди помітний (заголовки, кнопки).
 * У решті випадків достатньо маршрутного автозавантаження — див. docs/I18N.md.
 */
@Directive({
  selector: '[appTranslationModule]',
  standalone: true,
})
export class TranslationModuleDirective implements OnInit, OnDestroy {
  private readonly manager = inject(TranslationManagerService);
  private readonly templateRef = inject(TemplateRef<unknown>);
  private readonly viewContainer = inject(ViewContainerRef);
  private readonly cdRef = inject(ChangeDetectorRef);

  private readonly destroy$ = new Subject<void>();
  private rendered = false;

  @Input({ required: true }) public appTranslationModule!: TranslationModule;

  /** Рендерити вміст одразу, не чекаючи на файл (переклади доїдуть самі). */
  @Input() public appTranslationModuleEager = false;

  public ngOnInit(): void {
    if (this.appTranslationModuleEager) {
      this.render();
    }

    this.manager
      .loadModuleTranslations(this.appTranslationModule)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.render();
          this.cdRef.markForCheck();
        },
        // Модуль не доїхав — краще показати вміст із ключами, ніж порожній екран.
        error: () => this.render(),
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private render(): void {
    if (this.rendered) {
      return;
    }
    this.rendered = true;
    this.viewContainer.createEmbeddedView(this.templateRef);
  }
}
